import { useEffect, useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useSetAtom, useAtomValue } from "jotai";
import { PATHS } from "@/config/paths";
import { Card, CardContent } from "@/components/ui/card";
import {
  ChevronRight,
  CreditCard,
  Settings as SettingsIcon,
  User,
  LogOut,
  FileText,
  Bell,
  MessageSquare,
  Share2,
  Star,
  Users,
  Send,
  Trash,
  CalendarCog,
  Sparkles,
} from "lucide-react";
import { useTheme } from "@/components/themeProvider";
import { SunIcon, MoonIcon } from "lucide-react";
import { useLogoutInvalidate } from "@/hooks/useLogoutInvalidate";
import {
  APP_NAME,
  APP_STORE_URL,
  WHATSAPP_COMMUNITY_URL,
  GOOGLE_PLAY_URL,
} from "@/config/constants";
import { useUser } from "@/hooks/useUser";
import { loginDialog, registerDialog, isLoggingOutAtom, proDialog } from "@/state/dialogs";
import { PrivacyPolicyDrawer } from "@/components/drawers/PrivacyPolicyDrawer";
import { TermsOfServiceDrawer } from "@/components/drawers/TermsOfServiceDrawer";
import { Button } from "@/components/ui/button";
import { SocialDrawer } from "@/components/drawers/SocialDrawer";
import { useNotification } from "@/hooks/useNotification";
import { ConfirmDeleteDialog } from "@/components/dialogs/ConfirmDeleteDialog";
import { tryCatch } from "@/lib/try-catch";
import { deleteUser } from "@/lib/auth-client";
import { useMobileNavbar } from "@/providers/mobileNavbarProvider";
import { Container } from "@/components/ui/container";
import { initialOnboarding, onboardingAtom } from "@/state/onboarding";
import { useMobile } from "@/providers/mobileProvider";
import { cn } from "@/lib/utils";
import { AvatarSVG } from "@/components/ui/avatar";
import packageJson from "../../../package.json";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/dashboard/settings")({
  component: RouteComponent,
});

function RouteComponent() {
  const router = useRouter();
  const { logout } = useLogoutInvalidate();
  const { isAndroid } = useMobile();
  const isLoggingOut = useAtomValue(isLoggingOutAtom);
  const { user, hasPro, isAuthenticated, refetchUser } = useUser();
  const setLoginDialogOpen = useSetAtom(loginDialog);
  const setRegisterDialogOpen = useSetAtom(registerDialog);
  const setProDialogOpen = useSetAtom(proDialog);
  const [privacyPolicySheetOpen, setPrivacyPolicySheetOpen] = useState(false);
  const [termsOfServiceSheetOpen, setTermsOfServiceSheetOpen] = useState(false);
  const { setTheme, theme } = useTheme();
  const [socialDrawerOpen, setSocialDrawerOpen] = useState(false);
  const [openConfirmDeleteDialog, setConfirmDeleteDialogOpen] = useState(false);
  const { showNotification } = useNotification();
  const { setMobileNavbar } = useMobileNavbar();
  const setOnboarding = useSetAtom(onboardingAtom);
  const { t } = useTranslation();

  const rateUs = () => {
    if (isAndroid) {
      window.open(GOOGLE_PLAY_URL, "_blank");
    } else {
      // @TODO move to internal function
      window.open(APP_STORE_URL, "_blank");
    }
  };

  // Set up navbar configuration
  useEffect(() => {
    setMobileNavbar({
      title: t("settings.title"),
      showBackButton: true,
    });
  }, [setMobileNavbar]);

  const handleDelete = async () => {
    // Reset onboarding state
    setOnboarding(initialOnboarding);

    const { error } = await tryCatch(deleteUser());

    if (error) {
      showNotification({
        type: "error",
        message: t("settings.delete_account.error_title"),
        description: t("settings.delete_account.error_description"),
      });
      console.error("Error deleting account:", error);
    }

    refetchUser();

    showNotification({
      type: "success",
      message: t("settings.delete_account.success_title"),
      description: t("settings.delete_account.success_description"),
    });
  };

  return (
    <Container>
      <ConfirmDeleteDialog
        open={openConfirmDeleteDialog}
        onOpenChange={setConfirmDeleteDialogOpen}
        onConfirm={() => handleDelete()}
      />

      {/* Profile Section */}
      {isAuthenticated ? (
        <Card className="col-span-12 overflow-hidden">
          <CardContent className="relative p-0">
            <div className="absolute inset-0 bg-linear-to-br from-primary/10 via-primary/5 to-transparent" />
            <button
              type="button"
              onClick={() => router.navigate({ to: PATHS.PROFILE })}
              className="relative w-full p-6 flex items-center gap-4 hover:bg-accent/30 active:bg-accent/50 transition-colors text-left"
            >
              {/* Avatar */}
              <div
                className={cn("relative", {
                  "ring-2 ring-[hsl(var(--pro-ring))] ring-offset-2 ring-offset-background rounded-full":
                    hasPro,
                })}
              >
                <div className="size-16 rounded-full overflow-hidden bg-muted">
                  {user?.avatar ? (
                    <AvatarSVG seed={user.avatar} size={64} />
                  ) : (
                    <div className="size-full flex items-center justify-center bg-primary text-primary-foreground font-semibold text-lg">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                  )}
                </div>
                {hasPro && (
                  <div className="absolute -top-1 -right-1 size-6 rounded-full bg-[hsl(var(--pro-ring))] flex items-center justify-center">
                    <Star className="size-3 fill-white text-white" />
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h2
                    className={cn("text-lg font-semibold truncate", {
                      "text-[hsl(var(--pro-text))]": hasPro,
                    })}
                  >
                    {user?.name || "User"}
                  </h2>
                  {hasPro && (
                    <Sparkles className="size-4 text-[hsl(var(--pro-ring))] flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground truncate mb-2">{user?.email}</p>
                <div className="flex items-center gap-2">
                  <span
                    className={cn("text-xs px-2 py-1 rounded-full", {
                      "bg-[hsl(var(--pro-ring))]/10 text-[hsl(var(--pro-text))]": hasPro,
                      "bg-muted text-muted-foreground": !hasPro,
                    })}
                  >
                    {hasPro ? t("settings.pro_member") : t("settings.free_member")}
                  </span>
                </div>
              </div>

              {/* Edit Button */}
              <ChevronRight className="size-5 text-muted-foreground flex-shrink-0" />
            </button>
          </CardContent>
        </Card>
      ) : (
        <Card className="col-span-12 overflow-hidden">
          <CardContent className="relative p-6">
            <div className="absolute inset-0 bg-linear-to-br from-primary/10 via-primary/5 to-transparent" />
            <div className="relative text-center">
              <h2 className="text-lg font-semibold mb-2">
                {t("settings.welcome_to")} {APP_NAME}
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                {t("settings.sign_in_description")}
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  data-umami-event="settings-content-login"
                  onClick={() => setLoginDialogOpen(true)}
                  variant="outline"
                >
                  {t("settings.login")}
                </Button>
                <Button
                  data-umami-event="settings-content-register"
                  onClick={() => setRegisterDialogOpen(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {t("settings.sign_up")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account Section */}
      {isAuthenticated ? (
        <div className="col-span-12 mb-8">
          <Heading level="h2" size="h6" className="px-1 text-base font-medium mb-3">
            {t("settings.account")}
          </Heading>
          <div className="rounded-lg overflow-hidden bg-card/50">
            <button
              data-umami-event="settings-content-profile-settings"
              type="button"
              onClick={() => router.navigate({ to: PATHS.PROFILE })}
              className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <User className="size-5 text-primary" />
                <span>{t("settings.profile_settings")}</span>
              </div>
              <ChevronRight className="size-5 text-muted-foreground" />
            </button>
            <button
              data-umami-event="settings-content-account-settings"
              type="button"
              onClick={() => router.navigate({ to: PATHS.ACCOUNT })}
              className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <SettingsIcon className="size-5 text-primary" />
                <span>{t("settings.account_settings")}</span>
              </div>
              <ChevronRight className="size-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      ) : (
        <div className="col-span-12 mb-8">
          <Heading level="h2" size="h6" className="px-1 text-base font-medium mb-3">
            {t("settings.account")}
          </Heading>
          <div className="rounded-lg overflow-hidden bg-card/50">
            <button
              data-umami-event="settings-content-login"
              type="button"
              onClick={() => setLoginDialogOpen(true)}
              className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <LogOut className="size-5 text-primary rotate-180" />
                <span>{t("settings.login")}</span>
              </div>
              <ChevronRight className="size-5 text-muted-foreground" />
            </button>
            <button
              data-umami-event="settings-content-register"
              type="button"
              onClick={() => setRegisterDialogOpen(true)}
              className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <User className="size-5 text-primary" />
                <span>{t("settings.register")}</span>
              </div>
              <ChevronRight className="size-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      )}

      {/* Achieve More Section */}
      {!hasPro && (
        <div className="col-span-12 mb-8">
          <Heading level="h2" size="h6" className="px-1 text-base font-medium mb-3">
            {t("settings.achieve_more")}
          </Heading>
          <div className="rounded-lg overflow-hidden bg-card/50">
            <button
              type="button"
              data-umami-event="settings-content-upgrade-to-pro"
              onClick={() => setProDialogOpen(true)}
              className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <CreditCard className="size-5 text-primary" />
                <span>{t("settings.upgrade_to_pro")}</span>
              </div>
              <ChevronRight className="size-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      )}

      {/* Preferences Section */}
      <div className="col-span-12 mb-8">
        <Heading level="h2" size="h6" className="px-1 text-base font-medium mb-3">
          {t("settings.preferences")}
        </Heading>
        <div className="rounded-lg overflow-hidden bg-card/50">
          <button
            type="button"
            data-umami-event="settings-content-notification-settings"
            onClick={() => router.navigate({ to: PATHS.NOTIFICATION_SETTINGS })}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Bell className="size-5 text-primary" />
              <span>{t("settings.notification_settings")}</span>
            </div>
            <ChevronRight className="size-5 text-muted-foreground" />
          </button>
          <button
            type="button"
            data-umami-event="settings-content-calendar-settings"
            onClick={() => router.navigate({ to: PATHS.CALENDAR_SETTINGS })}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <CalendarCog className="size-5 text-primary" />
              <span>{t("settings.calendar_settings")}</span>
            </div>
            <ChevronRight className="size-5 text-muted-foreground" />
          </button>
          <button
            data-umami-event="settings-content-theme"
            type="button"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <SettingsIcon className="size-5 text-primary" />
              <span>{t("settings.theme")}</span>
            </div>
            <div className="flex items-center gap-2">
              <SunIcon className="size-5 hidden [html.dark_&]:block" />
              <MoonIcon className="size-5 hidden [html.light_&]:block" />
            </div>
          </button>
        </div>
      </div>

      {/* Support Section */}
      <div className="col-span-12 mb-8">
        <Heading level="h2" size="h6" className="px-1 text-base font-medium mb-3">
          {t("settings.support")}
        </Heading>
        <div className="rounded-lg overflow-hidden bg-card/50">
          <button
            data-umami-event="settings-content-contact-us"
            type="button"
            onClick={() => router.navigate({ to: PATHS.CONTACT })}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Send className="size-5 text-primary" />
              <span>{t("settings.contact")}</span>
            </div>
            <ChevronRight className="size-5 text-muted-foreground" />
          </button>
          <button
            data-umami-event="settings-content-join-whatsapp-community"
            type="button"
            onClick={() => window.open(WHATSAPP_COMMUNITY_URL, "_blank")}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <MessageSquare className="size-5 text-primary" />
              <span>{t("settings.join_whatsapp_community")}</span>
            </div>
            <ChevronRight className="size-5 text-muted-foreground" />
          </button>
          <SocialDrawer
            open={socialDrawerOpen}
            onOpenChange={setSocialDrawerOpen}
            trigger={
              <button
                data-umami-event="settings-content-join-whatsapp-community"
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Users className="size-5 text-primary" />
                  <span>{t("settings.follow_social_media")}</span>
                </div>
                <ChevronRight className="size-5 text-muted-foreground" />
              </button>
            }
          />

          <button
            data-umami-event="settings-content-rate-us"
            type="button"
            onClick={() => rateUs()}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Star className="size-5 text-primary" />
              <span>{t("settings.rate_us")}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
              </div>
              <ChevronRight className="size-5 text-muted-foreground" />
            </div>
          </button>

          <button
            data-umami-event="settings-content-share-with-friends"
            type="button"
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: APP_NAME,
                  text: t("settings.share_text", { appName: APP_NAME }),
                  url: window.location.origin,
                });
              }
            }}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Share2 className="size-5 text-primary" />
              <span>{t("settings.share_with_friends")}</span>
            </div>
            <ChevronRight className="size-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Resources Section */}
      <div className="col-span-12 mb-8">
        <Heading level="h2" size="h6" className="px-1 text-base font-medium mb-3">
          {t("settings.resources")}
        </Heading>
        <div className="rounded-lg overflow-hidden bg-card/50">
          <button
            data-umami-event="settings-content-blog"
            type="button"
            onClick={() => router.navigate({ to: PATHS.BLOG })}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <FileText className="size-5 text-primary" />
              <span>{t("settings.blog")}</span>
            </div>
            <ChevronRight className="size-5 text-muted-foreground" />
          </button>
          <button
            data-umami-event="settings-content-changelog"
            type="button"
            onClick={() => router.navigate({ to: PATHS.CHANGELOG })}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <FileText className="size-5 text-primary" />
              <span>{t("settings.changelog")}</span>
            </div>
            <ChevronRight className="size-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* More Section */}
      <div className="col-span-12 mb-8">
        <Heading level="h2" size="h6" className="px-1 text-base font-medium mb-3">
          {t("settings.more")}
        </Heading>
        <div className="rounded-lg overflow-hidden bg-card/50">
          <PrivacyPolicyDrawer
            open={privacyPolicySheetOpen}
            onOpenChange={setPrivacyPolicySheetOpen}
            trigger={
              <button
                data-umami-event="settings-content-privacy-policy"
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="size-5 text-primary" />
                  <span>{t("privacy_policy.title")}</span>
                </div>
                <ChevronRight className="size-5 text-muted-foreground" />
              </button>
            }
          />
          <TermsOfServiceDrawer
            open={termsOfServiceSheetOpen}
            onOpenChange={setTermsOfServiceSheetOpen}
            trigger={
              <button
                data-umami-event="settings-content-terms-of-service"
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="size-5 text-primary" />
                  <span>{t("terms_of_service.title")}</span>
                </div>
                <ChevronRight className="size-5 text-muted-foreground" />
              </button>
            }
          />
          {isAuthenticated && !isLoggingOut && (
            <button
              data-umami-event="settings-content-logout"
              type="button"
              onClick={() => logout()}
              className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors text-destructive"
            >
              <div className="flex items-center gap-3">
                <LogOut className="size-5 text-destructive" />
                <span>{t("settings.logout")}</span>
              </div>
              <ChevronRight className="size-5" />
            </button>
          )}
          {isAuthenticated && !isLoggingOut && (
            <button
              data-umami-event="settings-content-delete-account"
              type="button"
              onClick={() => setConfirmDeleteDialogOpen(true)}
              className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/30 active:bg-accent/50 transition-colors text-destructive"
            >
              <div className="flex items-center gap-3">
                <Trash className="size-5 text-destructive" />
                <span>{t("settings.delete_account.title")}</span>
              </div>
              <ChevronRight className="size-5" />
            </button>
          )}
        </div>
      </div>

      <div className="col-span-12 mt-4 text-center text-xs text-muted-foreground">
        {APP_NAME} for {isAndroid ? "Android" : "iOS"} v{packageJson.version}
      </div>
    </Container>
  );
}
